import React, { useState, useEffect, useRef } from 'react';

function degToRad(angle) {
  return (angle * Math.PI) / 180;
}
let midAngle;

function describeArc(cx, cy, rx, ry, startAngle, endAngle) {
  const start = {
    x: cx + rx * Math.cos(degToRad(startAngle)),
    y: cy + ry * Math.sin(degToRad(startAngle)),
  };
  const end = {
    x: cx + rx * Math.cos(degToRad(endAngle)),
    y: cy + ry * Math.sin(degToRad(endAngle)),
  };
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return [`M ${start.x} ${start.y}`, `A ${rx} ${ry} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`].join(
    ' '
  );
}

const ELLIPSE_RX = 250; // Radius of the ellipse along the x-axis
const ELLIPSE_RY = 150; // Radius of the ellipse along the y-axis
const CHART_WIDTH = 460; // Width of the chart
const CHART_HEIGHT = 300; // Height of the chart
const GAP_ANGLE = 10; // Gap between arcs

export const EllipticalSegmentChart = ({ segments, year, subtitle }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [showAllStats, setShowAllStats] = useState(true);
  const chartRef = useRef(null);
  let currentAngle = -80;

  // Calculate the total percentage to normalize the arc sizes
  const totalPercentage = segments.reduce((sum, segment) => sum + segment.percentage, 0);

  // Map segments to include start and end angles based on their percentage
  const chartSegments = segments.map((segment) => {
    const arcSweep = (segment.percentage / totalPercentage) * (360 - segments.length * GAP_ANGLE);
    const startAngle = currentAngle;
    const endAngle = currentAngle + arcSweep;
    currentAngle = endAngle + GAP_ANGLE;
    return { ...segment, startAngle, endAngle };
  });

  // Intersection Observer setup
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting) {
          setShowAllStats(true);
          // Start timer to switch to animation mode
          const timer = setTimeout(() => {
            setShowAllStats(false);
          }, 3000);
          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.5 }
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
    };
  }, []);

  const handleHoverAction = (i) => {
    setHoveredIdx(i);
    setActiveIdx(i);
  };

  // Interval logic for automatic arc highlighting
  useEffect(() => {
    if (hoveredIdx !== null || showAllStats || !isInView) return;
    const interval = setInterval(() => {
      setActiveIdx((prevIdx) => (prevIdx + 1) % chartSegments.length);
    }, 1000);
    return () => clearInterval(interval);
  }, [hoveredIdx, chartSegments.length, showAllStats, isInView]);

  return (
    <div
      ref={chartRef}
      className="relative flex min-h-[400px] flex-col items-center justify-center overflow-visible rounded-xl"
    >
      <div
        className="absolute inset-0 z-0"
        style={{
          borderRadius: '329px',
          background: '#F00037',
          filter: 'blur(400px)',
          width: '100%',
          height: '100%',
          transform: 'translateY(-50%)',
        }}
      ></div>
      <svg
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="relative block overflow-visible"
      >
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="#F00037" stopOpacity="1" />
            <stop offset="100%" stopColor="rgba(240, 0, 55, 0)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <defs>
          <linearGradient id="lineGradienttemp" x1="100%" y1="50%" x2="0%" y2="50%">
            <stop offset="0%" stopColor="#F00037" stopOpacity="1" />
            <stop offset="100%" stopColor="rgba(240, 0, 55, 0)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {chartSegments.map((seg, i) => (
          <path
            key={seg.label + seg.percentage}
            d={describeArc(
              CHART_WIDTH / 2,
              CHART_HEIGHT / 2,
              ELLIPSE_RX,
              ELLIPSE_RY,
              seg.startAngle,
              seg.endAngle
            )}
            stroke={
              showAllStats
                ? 'rgba(230, 0, 55, 0.7)'
                : hoveredIdx === i
                ? '#F00037'
                : activeIdx === i
                ? '#F00037'
                : '#fff'
            }
            strokeWidth={18}
            fill="none"
            strokeLinecap="round"
            style={{
              filter:
                showAllStats || hoveredIdx === i || activeIdx === i
                  ? 'drop-shadow(0 10px 15px rgba(240, 0, 55, 0.5))'
                  : 'drop-shadow(0 6px 10px rgba(0, 0, 0, 0.2))',
              transition: 'stroke 0.2s, filter 0.2s',
              cursor: 'pointer',
            }}
            onMouseEnter={() => handleHoverAction(i)}
            onMouseLeave={() => {
              if (hoveredIdx !== null) setActiveIdx(hoveredIdx);
              setHoveredIdx(null);
            }}
          />
        ))}

        {showAllStats
          ? // Show all stats
            chartSegments.map((seg, i) => {
              midAngle = (seg.startAngle + seg.endAngle) / 2;
              let sx = CHART_WIDTH / 2 + ELLIPSE_RX * Math.cos(degToRad(midAngle));
              let sy = CHART_HEIGHT / 2 + ELLIPSE_RY * Math.sin(degToRad(midAngle));
              const ex = CHART_WIDTH / 2 + (ELLIPSE_RX + 80) * Math.cos(degToRad(midAngle));
              const ey = CHART_HEIGHT / 2 + (ELLIPSE_RY + 80) * Math.sin(degToRad(midAngle));

              if (seg.percentage === 2) {
                sx = -31;
                sy = 151;
              }
              if (seg.percentage === 46) {
                sx = 240.044624;
                sy = 309.766115;
              }
              if (seg.percentage === 28) {
                sx = 452;
              }
              if (seg.percentage === 24) {
                sx = 51;
              }

              let tooltipX = ex;
              let tooltipY = ey;

              if (midAngle > 90 || midAngle < -90) {
                tooltipX -= 160;
                tooltipY += 10;
              } else {
                tooltipX -= 10;
                tooltipY -= 10;
              }

              if (midAngle > 170 || midAngle < -170) {
                tooltipY -= 40;
              }
              if (midAngle >= 0 && midAngle <= -10) {
                tooltipX += 5;
                tooltipY -= 10;
              }

              if (
                (midAngle === 115.60000000000001 &&
                  tooltipX === 87.4117028953458 &&
                  tooltipY === 357.42148107317615) ||
                (midAngle === 167.2 &&
                  tooltipX === -91.79928692083587 &&
                  tooltipY === 200.95615445247748)
              ) {
                tooltipX -= 30;
                tooltipY -= 20;
              }
           

              return (
                <g key={i}>
                  <line
                    x1={sx}
                    y1={sy}
                    x2={ex}
                    y2={ey}
                    stroke={showAllStats ? 'rgba(230, 0, 55, 0.7)' : 'url(#lineGradient)'}
               
                    strokeWidth={7}
                    strokeLinecap="round"
                    style={{
                      filter: 'drop-shadow(0 0 8px rgba(240, 0, 55, 0.4))',
                      opacity: 0.82,
                    }}
                  />
                  <foreignObject
                    x={tooltipX}
                    y={tooltipY - 20}
                    width={180}
                    height={50}
                    style={{ pointerEvents: 'none' }}
                  >
                    <div
                      className="flex items-center z-999"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        flexDirection:
                          midAngle > 90 || midAngle < -90
                            ? 'row'
                            : midAngle >= 0 && midAngle <= -10
                            ? 'row'
                            : 'row-reverse',
                      }}
                    >
                      <div
                        className="rounded-md bg-[#FFDAE2] px-4 py-2 text-center font-gellix shadow-md"
                        style={{
                          borderRadius: '4px',
                          background: '#FFDAE2',
                          padding: '8px 16px',
                          zIndex: 10,
                        }}
                      >
                        <span
                          className="mt-4 font-proxima-nova-bold text-sm font-semibold text-[#1F1F1F]"
                          style={{
                            fontSize: '14px',
                          }}
                        >
                          {seg.label}
                        </span>
                      </div>
                      <span
                        className="font-bold text-[#F00037]"
                        style={{
                          fontSize: '24px',
                        }}
                      >
                        {seg.percentage}%
                      </span>
                    </div>
                  </foreignObject>
                </g>
              );
            })
          : // Show single active stat
          hoveredIdx !== null || activeIdx !== null
          ? (() => {
              const seg = chartSegments[hoveredIdx !== null ? hoveredIdx : activeIdx];
              midAngle = (seg.startAngle + seg.endAngle) / 2;

              // Start point of the line
              const sx = CHART_WIDTH / 2 + ELLIPSE_RX * Math.cos(degToRad(midAngle));
              const sy = CHART_HEIGHT / 2 + ELLIPSE_RY * Math.sin(degToRad(midAngle));

              // End point of the line (moved further away for better spacing)
              const ex = CHART_WIDTH / 2 + (ELLIPSE_RX + 80) * Math.cos(degToRad(midAngle));
              const ey = CHART_HEIGHT / 2 + (ELLIPSE_RY + 80) * Math.sin(degToRad(midAngle));

              if (seg.percentage === 2) {
                sx = -19;
                sy = 151;
              }
              if (seg.percentage === 46) {
                sx = 240.044624;
                sy = 309.766115;
              }
              if (seg.percentage === 2) {
                sx = -31;
                sy = 151;
              }
              if (seg.percentage === 46) {
                sx = 240.044624;
                sy = 309.766115;
              }
              if (seg.percentage === 28) {
                sx = 452;
              }
              if (seg.percentage === 24) {
                sx = 51;
              }

              // Tooltip position (adjusted dynamically based on the angle)
              let tooltipX = ex;
              let tooltipY = ey;

              // Adjust tooltip position for left-side arcs
              if (midAngle > 90 || midAngle < -90) {
                tooltipX -= 160; // Move tooltip further left
                tooltipY += 10; // Adjust vertical alignment
              } else {
                tooltipX -= 10; // Move tooltip slightly right
                tooltipY -= 10;
              }

              // Adjust tooltip position for bottom arcs
              if (midAngle > 170 || midAngle < -170) {
                tooltipY -= 40; // Move tooltip upward for bottom arcs
              }
              if (midAngle >= 0 && midAngle <= -10) {
                tooltipX += 5; // Fine-tune position for arcs in this range
                tooltipY -= 10; // Slightly adjust vertical alignment
              }

              if (
                (midAngle === 115.60000000000001 &&
                  tooltipX === 87.4117028953458 &&
                  tooltipY === 357.42148107317615) ||
                (midAngle === 167.2 &&
                  tooltipX === -91.79928692083587 &&
                  tooltipY === 200.95615445247748)
              ) {
                tooltipX -= 30; // Reduce tooltipX for better alignment
                tooltipY -= 20; // Reduce tooltipY for better alignment
              }
             
              return (
                <g>
                  {/* Connecting line with gradient */}
                  <line
                    x1={sx}
                    y1={sy}
                    x2={ex}
                    y2={ey}
                    // stroke={showAllStats ? 'rgba(230, 0, 55, 0.7)' : 'url(#lineGradient)'}
                    stroke={sx < ex ? 'url(#lineGradient)' : 'url(#lineGradienttemp)'}
                    strokeWidth={7}
                    strokeLinecap="round"
                    style={{
                      filter: 'drop-shadow(0 0 8px rgba(240, 0, 55, 0.4))',
                      opacity: 0.82,
                    }}
                  />

                  {/* Percentage before the label box */}
                  <foreignObject
                    x={tooltipX}
                    y={tooltipY - 20} // Adjust for vertical alignment
                    width={180} // Adjusted width to fit both percentage and label
                    height={50} // Increased height for better visibility
                    style={{ pointerEvents: 'none' }}
                  >
                    <div
                      className="flex items-center"
                      style={{
                        display: 'flex', // Flexbox for horizontal alignment
                        alignItems: 'center', // Center content vertically
                        gap: '20px', // Reduced gap for arcs between 0 and -10
                        flexDirection:
                          midAngle > 90 || midAngle < -90
                            ? 'row'
                            : midAngle >= 0 && midAngle <= -10
                            ? 'row'
                            : 'row-reverse',
                      }}
                    >
                      {/* Percentage */}

                      {/* Label box */}
                      <div
                        className="rounded-md bg-[#FFDAE2] px-4 py-2 text-center font-gellix shadow-md"
                        style={{
                          borderRadius: '4px', // Rounded corners
                          background: '#FFDAE2', // Light pink background
                          padding: '8px 16px', // Consistent padding
                          zIndex: 10, // Ensure the box is above other components
                        }}
                      >
                        <span
                          className="mt-4 font-proxima-nova-bold text-sm font-semibold text-[#1F1F1F]"
                          style={{
                            fontSize: '14px', // Slightly smaller font for the label
                          }}
                        >
                          {seg.label}
                        </span>
                      </div>
                      <span
                        className="font-bold text-[#F00037]"
                        style={{
                          fontSize: '24px', // Increased font size for the percentage
                        }}
                      >
                        {seg.percentage}%
                      </span>
                    </div>
                  </foreignObject>
                </g>
              );
            })()
          : null}
        {(hoveredIdx !== null || activeIdx !== null) && !showAllStats && (() => {
          const idx = hoveredIdx !== null ? hoveredIdx : activeIdx;
          const seg = chartSegments[idx];
          return (
            <path
              d={describeArc(
                CHART_WIDTH / 2,
                CHART_HEIGHT / 2,
                ELLIPSE_RX,
                ELLIPSE_RY,
                seg.startAngle,
                seg.endAngle
              )}
              stroke="#F00037"
              strokeWidth={22} // slightly larger for emphasis
              fill="none"
              strokeLinecap="round"
              style={{
                filter: 'drop-shadow(0 10px 15px rgba(240, 0, 55, 0.5))',
                transition: 'stroke 0.2s, filter 0.2s',
                cursor: 'pointer',
                pointerEvents: 'none', // so it doesn't block the original arc's events
              }}
            />
          );
        })()}
      </svg>
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 select-none"
        style={{
          transform: 'translate(-50%, -38%)',
          minWidth: 220,
        }}
      >
        <div className="mb-1 text-center font-gellix-regular text-lg font-medium text-[#F00037] opacity-80">
          {subtitle}
        </div>
        <div className="text-center font-proxima-nova-bold text-4xl font-medium text-gray-900 drop-shadow">
          {year}
        </div>
      </div>
    </div>
  );
};

export default EllipticalSegmentChart;
